/**
 * Line chart time axis: parse dates from survey cells and build Widget.Query __lineChartTime JSON.
 */

/**
 * True if at least minRatio of non-empty sample cells parse as a date/datetime (time is ignored for bucketing).
 */
export function columnLooksLikeDateTime(sampleRows, getCellValue, columnName, minRatio = 0.8, maxSample = 800) {
  if (!sampleRows?.length || !columnName) return false;
  let ok = 0;
  let n = 0;
  const cap = Math.min(sampleRows.length, maxSample);
  for (let i = 0; i < cap; i += 1) {
    const v = getCellValue(sampleRows[i], columnName);
    if (v == null || String(v).trim() === '') continue;
    n += 1;
    if (parseSurveyDate(v) != null) ok += 1;
  }
  if (n === 0) return false;
  return ok / n >= minRatio;
}

/** Merge analyzer date columns with columns whose sample values parse as datetime (e.g. text column with time). */
export function mergeDateLikeColumnNames(allColumnNames, sampleRows, getCellValue, analyzerDateNames) {
  const set = new Set(analyzerDateNames.filter(Boolean));
  for (const col of allColumnNames) {
    if (set.has(col)) continue;
    if (columnLooksLikeDateTime(sampleRows, getCellValue, col)) set.add(col);
  }
  return [...set];
}

export function parseSurveyDate(val) {
  if (val == null || val === '') return null;
  if (val instanceof Date && !Number.isNaN(val.getTime())) return val;
  const s = String(val).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(`${s.slice(0, 10)}T12:00:00`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (s.includes('T')) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d;
  }
  const slash = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (slash) {
    const a = +slash[1];
    const b = +slash[2];
    const y = +slash[3];
    const dUs = new Date(y, b - 1, a, 12, 0, 0);
    const dEu = new Date(y, a - 1, b, 12, 0, 0);
    if (!Number.isNaN(dUs.getTime()) && dUs.getFullYear() === y) return dUs;
    if (!Number.isNaN(dEu.getTime()) && dEu.getFullYear() === y) return dEu;
  }
  const d2 = new Date(s);
  return Number.isNaN(d2.getTime()) ? null : d2;
}

/**
 * @param {string} [existingQuery] - prior Widget.Query JSON string
 * @param {object} cfg
 * @param {'year'|'month'|'day'} cfg.granularity
 * @param {'all'|'filtered'} cfg.scope
 * @param {number|null} [cfg.filterYear]
 * @param {number|null} [cfg.filterMonth] 1-12
 * @param {number|null} [cfg.filterYearStart]
 * @param {number|null} [cfg.filterYearEnd]
 */
export function mergeLineChartTimeIntoQuery(existingQuery, cfg) {
  let base = {};
  if (existingQuery && typeof existingQuery === 'string' && existingQuery.trim().startsWith('{')) {
    try {
      base = JSON.parse(existingQuery);
    } catch {
      base = {};
    }
  }
  base.__lineChartTime = {
    granularity: cfg.granularity,
    scope: cfg.scope,
    filterYear: cfg.filterYear ?? null,
    filterMonth: cfg.filterMonth ?? null,
    filterYearStart: cfg.filterYearStart ?? null,
    filterYearEnd: cfg.filterYearEnd ?? null,
  };
  return JSON.stringify(base);
}

export function parseLineChartTimeFromQuery(queryStr) {
  if (!queryStr || typeof queryStr !== 'string' || !queryStr.trim().startsWith('{')) return null;
  try {
    const meta = JSON.parse(queryStr);
    return meta.__lineChartTime && typeof meta.__lineChartTime === 'object'
      ? meta.__lineChartTime
      : null;
  } catch {
    return null;
  }
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function monthMenuItems() {
  return MONTH_NAMES.map((label, i) => ({ value: i + 1, label }));
}

/**
 * Aggregate rows for line chart preview (matches API/widgets/line_chart_time.py).
 */
export function aggregateLineChartPreview(rows, getCellValue, xCol, yCol, aggregation, timeCfg) {
  const granularity = ['year', 'month', 'day'].includes((timeCfg.granularity || '').toLowerCase())
    ? timeCfg.granularity.toLowerCase()
    : 'day';
  const scope = (timeCfg.scope || 'all').toLowerCase() === 'filtered' ? 'filtered' : 'all';
  const fy = timeCfg.filterYear != null ? Number(timeCfg.filterYear) : null;
  const fm = timeCfg.filterMonth != null ? Number(timeCfg.filterMonth) : null;
  const fys = timeCfg.filterYearStart != null ? Number(timeCfg.filterYearStart) : null;
  const fye = timeCfg.filterYearEnd != null ? Number(timeCfg.filterYearEnd) : null;

  const agg = (aggregation || 'average').toLowerCase() === 'avg' ? 'average' : (aggregation || 'average').toLowerCase();

  const series = [];
  for (const row of rows) {
    const rawX = getCellValue(row, xCol);
    const dt = parseSurveyDate(rawX);
    if (!dt) continue;

    if (scope === 'filtered') {
      if (granularity === 'day') {
        if (fy != null && !Number.isNaN(fy) && dt.getFullYear() !== fy) continue;
        if (fm != null && !Number.isNaN(fm) && dt.getMonth() + 1 !== fm) continue;
      } else if (granularity === 'month') {
        if (fy != null && !Number.isNaN(fy) && dt.getFullYear() !== fy) continue;
      } else if (granularity === 'year') {
        if (fys != null && !Number.isNaN(fys) && dt.getFullYear() < fys) continue;
        if (fye != null && !Number.isNaN(fye) && dt.getFullYear() > fye) continue;
      }
    }

    let label;
    let sortKey;
    if (granularity === 'year') {
      label = String(dt.getFullYear());
      sortKey = dt.getFullYear();
    } else if (granularity === 'month') {
      label = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      sortKey = dt.getFullYear() * 100 + dt.getMonth() + 1;
    } else {
      label = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      sortKey = dt.getTime();
    }

    const rawY = getCellValue(row, yCol);
    series.push({ label, sortKey, rawY });
  }

  if (series.length === 0) return [];

  const parseNum = (val) => {
    if (typeof val === 'number' && !Number.isNaN(val)) return val;
    if (val == null || String(val).trim() === '') return null;
    const n = parseFloat(String(val).replace(/,/g, '').trim());
    return Number.isNaN(n) ? null : n;
  };
  const parsedYs = series.map((s) => parseNum(s.rawY));
  const allNumeric = parsedYs.every((p) => p != null);

  const buckets = new Map();
  if (allNumeric) {
    series.forEach((s, i) => {
      const pv = parsedYs[i];
      if (pv == null) return;
      if (!buckets.has(s.label)) buckets.set(s.label, { vals: [], sortKey: s.sortKey });
      buckets.get(s.label).vals.push(pv);
    });
  } else {
    series.forEach((s) => {
      if (!buckets.has(s.label)) buckets.set(s.label, { vals: [], sortKey: s.sortKey });
      buckets.get(s.label).vals.push(1);
    });
  }

  const computeAgg = (vals) => {
    if (!vals.length) return 0;
    switch (agg) {
      case 'sum':
        return vals.reduce((a, b) => a + b, 0);
      case 'count':
        return vals.length;
      case 'min':
        return Math.min(...vals);
      case 'max':
        return Math.max(...vals);
      case 'median': {
        const sorted = [...vals].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
      }
      case 'average':
      default:
        return vals.reduce((a, b) => a + b, 0) / vals.length;
    }
  };

  const out = [...buckets.entries()].map(([name, { vals, sortKey }]) => ({
    name,
    value: parseFloat(computeAgg(vals).toFixed(4)),
    _sortKey: sortKey,
  }));
  out.sort((a, b) => a._sortKey - b._sortKey);
  return out.map(({ name, value }) => ({ name, value }));
}
